import React, { useMemo } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import Lam2RollsChart, {
  ROLL_COLORS,
  fmtRolls,
  fmtShare,
} from "../Lamination2/Lam2RollsChart";
import UnavailablePanel from "../../common/UnavailablePanel";
import { type RollBucket } from "../../../utils/rollBuckets";

/**
 * Roll volume per bucket, as a panel inside the roll analytics card.
 *
 * It owns no state and fetches nothing: the card above holds the line, the
 * grouping and the timeframe, and hands down the shifts they select. That is
 * what keeps this chart and the mix chart beneath it describing the same rolls
 * — two independent fetches would drift by a poll.
 */
type Props = {
  /**
   * The timeframe's shifts, bucketed by the card — the same list the mix panel
   * gets, so a period sits at the same x in both.
   */
  buckets: RollBucket[];
  loading: boolean;
  /** Why there is nothing real to show; null when there is. */
  unavailable: string | null;
  /** Shared y-axis ceiling, so neighbouring cards draw on the same scale. */
  yMax?: number;
};

const RollsPanel: React.FC<Props> = ({
  buckets,
  loading,
  unavailable,
  yMax,
}) => {
  // The chart still wants shift-shaped rows; the bucket supplies the naming it
  // could not work out from a single start date.
  const chartRows = useMemo(
    () =>
      buckets.map((b) => ({
        shift_id: b.key,
        shift_start: b.at.toISOString(),
        shift_hour: b.at.getHours(),
        shift_name: b.sublabel,
        a_rolls: b.a,
        b_rolls: b.b,
        f_rolls: b.f,
        total: b.total,
        updated_at: null,
        is_partial: b.is_partial,
        label: b.label,
        sublabel: b.sublabel,
      })),
    [buckets]
  );

  const totals = useMemo(() => {
    const t = buckets.reduce(
      (acc, b) => {
        acc.a += b.a;
        acc.b += b.b;
        acc.f += b.f;
        return acc;
      },
      { a: 0, b: 0, f: 0 }
    );
    return { ...t, all: t.a + t.b + t.f };
  }, [buckets]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Roll counts
      </Typography>

      {unavailable ? (
        <UnavailablePanel reason={unavailable}>
          <Lam2RollsChart rows={chartRows} height={280} compact />
        </UnavailablePanel>
      ) : (
        <LoadingOverlay show={loading} minHeight={260} label="Loading roll counts…">
          {buckets.every((b) => b.shifts === 0) && !loading ? (
            <Typography variant="body2" color="text.secondary">
              No shift roll counts recorded yet for this line.
            </Typography>
          ) : (
            <Lam2RollsChart rows={chartRows} height={280} compact yMax={yMax} />
          )}
        </LoadingOverlay>
      )}

      {/* The totals sit outside the blur, so they have to go too — real Lam 2
          counts under a template heading would be read as that selection's. */}
      {/* While loading these would read "0", which is a real (and alarming)
          count, so they wait under skeletons like the chart waits under its
          spinner. */}
      {!unavailable && loading && (
        <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
          {[56, 56, 56, 64].map((w, i) => (
            <Skeleton key={i} variant="text" width={w} />
          ))}
        </Stack>
      )}
      {!unavailable && !loading && (
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            A <b style={{ color: ROLL_COLORS.a }}>{fmtRolls(totals.a)}</b>{" "}
            {fmtShare(totals.a, totals.all)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            B <b style={{ color: ROLL_COLORS.b }}>{fmtRolls(totals.b)}</b>{" "}
            {fmtShare(totals.b, totals.all)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            F <b style={{ color: ROLL_COLORS.f }}>{fmtRolls(totals.f)}</b>{" "}
            {fmtShare(totals.f, totals.all)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total <b>{fmtRolls(totals.all)}</b>
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default RollsPanel;
